import { useEffect, useMemo, useState } from 'react';
import { RclButton, RclCollapse, RclCollapsePanel, RclList, RclListItem, RclSpace, RclText, RclPagination } from '@risepeopleinc/rcl-react'
import './OverrideMapping.scss'
import { IDepartment, OVERRIDE_TABS } from '../../../../interfaces/IJournalEntry';
import { useAppSelector } from '../../../../hooks/reduxHooks';
import { selectDepartments } from '../../../../reducers/journalEntrySlice';
import SelectDebitCredit from '../SelectDebitCredit/SelectDebitCredit';
import { useJEFormContext } from '../../../../hooks/useJEFormContext';
import { isMappingComplete } from '../../../../helpers';
import { selectPrimaryBankAccount } from '../../../../reducers/orgSlice';
import type { PaginationProps } from 'antd';

interface IPageStateProps {
  currentPage: number,
  pageSize: number,
  minValue: number,
  maxValue: number,
  total: number
}

export const DepartmentMapping = ({ selectedTab }: { selectedTab: any }) => {
  const departments = useAppSelector(selectDepartments)
  const [pageState, setPageState] = useState<IPageStateProps>({
    currentPage: 1,
    pageSize: 10,
    minValue: 0,
    maxValue: 10,
    total: departments.length
  })

  const updatePage: PaginationProps['onShowSizeChange'] = (currentPage, pageSize) => {
    setPageState((prev: IPageStateProps) => ({
      ...prev,
      currentPage: currentPage,
      pageSize: pageSize,
      minValue: (currentPage - 1) * pageSize,
      maxValue: currentPage * pageSize,
    }))
  };

  return (
    <>
      {(selectedTab === OVERRIDE_TABS.department) &&
        <>
          <RclList className="override-mapping-wrapper">
            {departments.slice(pageState.minValue, pageState.maxValue).map(department => (
              <DepartmentMappingItem key={department.id} department={department} />
            ))}
          </RclList>
          <RclPagination
            showSizeChanger
            current={pageState.currentPage}
            // @ts-ignore
            onChange={updatePage}
            total={pageState.total}
          />
        </>}
    </>
  )
}

interface IDepartmentMappingItemProps {
  department: IDepartment
}
const DepartmentMappingItem: React.FC<IDepartmentMappingItemProps> = ({ department }) => {
  const primaryBankAccount = useAppSelector(selectPrimaryBankAccount)
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const keyName = useMemo(() => `department${department.id.toString()}`, [department.id])
  const { state, setStateForKey } = useJEFormContext()
  const debitCredit = useMemo(() => state[keyName], [state, keyName])

  useEffect(() => {
    if (isMappingComplete(debitCredit))
      setIsExpanded(true);
  }, [debitCredit])

  const handleRemove = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setStateForKey(keyName, { credit: primaryBankAccount, debit: undefined })
    setIsExpanded(false)
  }

  return (
    <RclListItem className='override-mapping-item'>
      <RclCollapse activeKey={isExpanded ? department.id : undefined} onChange={() => setIsExpanded(s => !s)}>
        <RclCollapsePanel
          key={department.id}
          showArrow={false}
          {...{ className: 'override-mapping-collapse-panel' }}
          collapsible={isMappingComplete(debitCredit) ? 'disabled' : 'header'}
          header={
            <div className="override-mapping-item-header">
              <RclSpace>
                <RclText className='override-mapping-item-name'>{department.dept_name}</RclText>
                <RclText className='override-mapping-item-code'>{department.dept_code}</RclText>
              </RclSpace>
              <RclButton hidden={!isMappingComplete(debitCredit)} onClick={handleRemove} type='link' size='small' danger>Remove</RclButton>
              {!isMappingComplete(debitCredit) && !isExpanded && <RclText className='mapping-override-default-text'>Override default</RclText>}
            </div>
          }
        >
          <SelectDebitCredit keyName={keyName} showSubtitle={false} />
        </RclCollapsePanel>
      </RclCollapse>
    </RclListItem>
  )
}
